// packages/report-components/src/Period.tsx
//
// Renders a human-readable label for a Verity period expression.
//
// Matches the backend expression grammar declared in
// apps/backend/services/reports/periods.py — but DOES NOT resolve the
// expression (no date arithmetic). Resolution is a hook-resolver concern;
// this component's job is purely cosmetic.
//
// Input shape:
//   - string preset:  "last_quarter" | "last_7_days" | "ytd" | ...
//   - rolling string: "rolling_4_weeks" (W2; ADR-0267 — the spelling a
//                     block report's period carries; whole weeks/months)
//   - object:         { preset: "rolling_n_days", n: 28 }
//                     { preset: "custom", start: "2026-01-01", end: "2026-03-31" }
//
// Unknown presets render the raw value so a drift between backend and
// sandbox doesn't silently hide the data — the author will notice the
// literal "last_aeon" and fix it.

import * as React from "react";

export type PeriodExpr =
  | string
  | { preset: string; n?: number; start?: string; end?: string };

export interface PeriodProps {
  /** The period expression — string preset or parameterized object. */
  value: PeriodExpr;
  /** Optional prefix, rendered before the label ("as of", "during"). */
  prefix?: string;
  /** Render inline <span> vs block <time>. Defaults to inline. */
  inline?: boolean;
}

const STRING_PRESET_LABELS: Record<string, string> = {
  today: "Today",
  yesterday: "Yesterday",
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  last_quarter: "Last quarter",
  this_year: "This year",
  last_year: "Last year",
  ytd: "Year to date",
  mtd: "Month to date",
  wtd: "Week to date",
  last_7_days: "Last 7 days",
  last_14_days: "Last 14 days",
  last_28_days: "Last 28 days",
  last_30_days: "Last 30 days",
  last_90_days: "Last 90 days",
  last_365_days: "Last 365 days",
};

const ROLLING_UNIT: Record<string, string> = {
  rolling_n_days: "days",
  rolling_n_weeks: "weeks",
  rolling_n_months: "months",
};

/**
 * Format a period expression to a human-readable label.
 * Exported for re-use by other components (ChartFrame's data-source footer).
 */
const ROLLING_SPELLING = /^rolling_([1-9]\d{0,2})_(days|weeks|months)$/;

export function formatPeriod(value: PeriodExpr): string {
  if (typeof value === "string") {
    const rolling = ROLLING_SPELLING.exec(value);
    if (rolling) return `Last ${Number(rolling[1])} ${rolling[2]}`;
    return STRING_PRESET_LABELS[value] ?? humanize(value);
  }
  if (value && typeof value === "object") {
    const preset = value.preset;
    if (preset in ROLLING_UNIT) {
      const n = typeof value.n === "number" ? value.n : NaN;
      const unit = ROLLING_UNIT[preset];
      if (Number.isFinite(n)) {
        return `Last ${n} ${unit}`;
      }
      return `Last ${unit}`;
    }
    if (preset === "custom") {
      const start = value.start ?? "";
      const end = value.end ?? "";
      if (start && end) return `${start} — ${end}`;
      if (start) return `From ${start}`;
      if (end) return `Up to ${end}`;
      return "Custom range";
    }
    return STRING_PRESET_LABELS[preset] ?? humanize(preset);
  }
  return String(value);
}

function humanize(raw: string): string {
  if (!raw) return "";
  return raw
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

export function Period({
  value,
  prefix,
  inline = true,
}: PeriodProps): React.ReactElement {
  const label = formatPeriod(value);
  const full = prefix ? `${prefix} ${label}` : label;
  const style: React.CSSProperties = {
    color: "var(--verity-color-text-muted, #6b7280)",
    fontSize: "var(--verity-font-size-sm, 13px)",
    fontWeight: 500,
  };
  const props = {
    className: "verity-period",
    style,
    "data-preset":
      typeof value === "string" ? value : value?.preset ?? "unknown",
  };
  return inline
    ? React.createElement("span", props, full)
    : React.createElement("time", props, full);
}
